import Decimal from "decimal.js";

/**
 * AC-798: the single money-formatting utility.
 *
 * Currency rendering rule: only USD and EUR use their symbols ("$5", "€305.02").
 * Every other currency renders as a plain number followed by the 3-letter ISO
 * code ("7,500 ARS", "85 MXN") — Intl symbols for the dollar-family currencies
 * (MX$, R$, ARS$…) are ambiguous and blow up tight card layouts.
 *
 * Financial invariant: amounts are pre-rounded DOWN to the currency's minor
 * units with decimal.js BEFORE formatting. Intl rounds half-even and would
 * otherwise show money the player does not have.
 */

const SYMBOL_CURRENCIES = new Set(['USD', 'EUR']);

/** Non-breaking space between the amount and the ISO code — "5,999.82 ARS" is
 *  one visual token and must never wrap between the number and the code in
 *  tight card columns (a plain space did). */
const NBSP = '\u00A0';

const formatterCache = new Map<string, Intl.NumberFormat>();

const getFormatter = (locale: string, currency: string): Intl.NumberFormat => {
    const key = `${locale}:${currency}`;
    let formatter = formatterCache.get(key);
    if (!formatter) {
        const digits = getCurrencyFractionDigits(locale, currency);
        formatter = new Intl.NumberFormat(locale, SYMBOL_CURRENCIES.has(currency) ? {
            style: 'currency',
            currency,
            currencyDisplay: 'symbol',
            // Whole amounts render without the ".00" tail ("$5" instead of
            // "$5.00") — round per-currency bonus terms read cleaner.
            // Fractional amounts keep their minor units untouched. Engines
            // that predate the option silently ignore the property.
            trailingZeroDisplay: 'stripIfInteger',
        } as Intl.NumberFormatOptions : {
            // Non-symbol currencies: bare number, the ISO code is appended by
            // the caller-facing helpers below ("7,500 ARS").
            style: 'decimal',
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
            trailingZeroDisplay: 'stripIfInteger',
        } as Intl.NumberFormatOptions);
        formatterCache.set(key, formatter);
    }
    return formatter;
};

/** Minor units of a currency per Intl (PYG → 0, USD → 2). */
export const getCurrencyFractionDigits = (locale: string, currency: string): number => {
    try {
        return new Intl.NumberFormat(locale, {style: 'currency', currency})
            .resolvedOptions().maximumFractionDigits ?? 2;
    } catch {
        return 2;
    }
};

export type MoneyInput = number | string | Decimal;

export const formatMoney = (amount: MoneyInput, currency: string, locale: string): string => {
    const decimalAmount = new Decimal(amount || 0);
    try {
        const digits = getCurrencyFractionDigits(locale, currency);
        const truncated = decimalAmount.toDP(digits, Decimal.ROUND_DOWN);
        const formatted = getFormatter(locale, currency).format(truncated.toNumber());
        return SYMBOL_CURRENCIES.has(currency) ? formatted : `${formatted}${NBSP}${currency}`;
    } catch {
        // Unknown currency code (Intl throws RangeError) — degrade to a plain
        // "<amount> <code>" string rather than crashing the render.
        return `${decimalAmount.toDP(2, Decimal.ROUND_DOWN).toString()}${NBSP}${currency}`;
    }
};

/** Amounts at/above this render compactly ("14.9K ARS") in tight layouts. */
const COMPACT_THRESHOLD = 10_000;

const getCompactFormatter = (locale: string, currency: string): Intl.NumberFormat => {
    const key = `${locale}:${currency}:compact`;
    let formatter = formatterCache.get(key);
    if (!formatter) {
        formatter = new Intl.NumberFormat(locale, {
            ...(SYMBOL_CURRENCIES.has(currency)
                ? {style: 'currency', currency, currencyDisplay: 'symbol'}
                : {style: 'decimal'}),
            notation: 'compact',
            maximumFractionDigits: 1,
            // Never show money the player does not have; engines that predate
            // roundingMode silently ignore the property (half-even fallback).
            roundingMode: 'floor',
        } as Intl.NumberFormatOptions);
        formatterCache.set(key, formatter);
    }
    return formatter;
};

/**
 * AC-798: card/stat-row variant of formatMoney. Weak currencies produce
 * 6-8 digit amounts (224,985.46 ARS) that overflow fixed card columns, so
 * from 10k upward the amount renders compactly ("224.9K ARS", "2M PYG").
 * Below the threshold it behaves exactly like formatMoney. Detail surfaces
 * (modals, wagering progress) keep the full-precision formatter.
 */
export const formatMoneyCompact = (amount: MoneyInput, currency: string, locale: string): string => {
    const decimalAmount = new Decimal(amount || 0);
    if (decimalAmount.abs().lt(COMPACT_THRESHOLD)) {
        return formatMoney(amount, currency, locale);
    }
    try {
        const formatted = getCompactFormatter(locale, currency).format(decimalAmount.toNumber());
        return SYMBOL_CURRENCIES.has(currency) ? formatted : `${formatted}${NBSP}${currency}`;
    } catch {
        return formatMoney(amount, currency, locale);
    }
};
